import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ModalEditComponentProps } from '../../../../../../../../../../baseComponents/ModalsLayouts/types'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../../../../utils/globals/data'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { WorkingCenterEmployeeWithDefaultTy } from '../type'
import { ButtonSecondary } from '../../../../../../../../../../baseComponents/Button'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../../../../baseComponents/TwinTrans'
import { ModalOpenedSetTy } from '../../../../../../../../../../baseComponents/Modal/types'
import { OnSubmit } from '../../../../../../../../../../forms/TwinForm/types'
import { flushSync } from 'react-dom'
import { LittleModalCreateEdit } from '../../../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'

type ModalCreateEmployeeWorkingCenterProps = ModalEditComponentProps & ModalCreateEmployeeWorkingCenterLogicProps & {
    preSelEmployees: string[]
}

const ModalCreateEmployeeWorkingCenter: React.FC<ModalCreateEmployeeWorkingCenterProps> = ({ userPermissions, allRowData, dictWorkingCenterStations, ...rest }) => {
    const { t } = useTwinTranslation()
    const { employees, id, selectedEmployees, handleAddEmployee, handleRemoveEmployee, assignEmployeesToWorkingCenter, selectedEmployeeIds, changeFields, formData } = useModalCreateEmployeeWorkingCenterLogic({ dictWorkingCenterStations, ...rest })
    const extraField: FormElement<keyof WorkingCenterEmployeeWithDefaultTy>[] = []
    if (Object.keys(dictWorkingCenterStations).length) {
        extraField.push({
            name: 'defaultWorkingCenterStationId',
            label: t('workingCenterStation', 'Puesto'),
            component: 'CustomSelectWithSearchBar',
            items: dictWorkingCenterStations,
            onChange: (val) => changeFields('defaultWorkingCenterStationId', val),
            value: formData.defaultWorkingCenterStationId || '',
        },)
    }
    const fields: FormRender<WorkingCenterEmployeeWithDefaultTy> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'EmployeeId',
                    label: t('employee', 'Empleado'),
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    items: employees,
                    excludeItems: selectedEmployeeIds,
                    fieldName: 'fullname_short',
                    onChange: (val)=> changeFields('EmployeeId', val),
                    value: formData.EmployeeId || '',
                },
                ...extraField,
                {
                    name: 'is_default',
                    label: t('isDefault', 'Por defecto'),
                    component: 'Checkbox',
                    onChange: (val) => changeFields('is_default', val),
                    checked: formData.is_default
                },
                {
                    name: 'WorkingCenterId',
                    component: 'InputHidden',
                    value: id
                },
            ],
        },
    ]

    const renderThis: JSX.Element[] = []
    for (const key in selectedEmployees) {
        const employee = selectedEmployees[key]
        renderThis.push(<EmployeeSelected name={employees?.[employee.EmployeeId]?.fullname_short} posArr={parseInt(key)} removeEmployee={handleRemoveEmployee} EmployeeId={String(employee.EmployeeId)} />)
    }
    return (
    <LittleModalCreateEdit fields={fields} url='' translations={{
        title: t('addUsers', 'Añadir usuarios'),
        button: 'Añadir usuarios'
        }} haveButtonPermissions={selectedEmployees?.length? true: false} {...rest} onSubmit={() => { }} beforeSubmit={() => { assignEmployeesToWorkingCenter(); return false }} className='notFlexAutoFormRender'>
        <ButtonSecondary className='mt-20 w-100 ml-auto' onClick={handleAddEmployee} type='button'>
            <TwinTrans transKey='add'>Añadir</TwinTrans>
        </ButtonSecondary>
        <div className='my-20 regular18'>
            <TwinTrans transKey='users'>Usuarios</TwinTrans>
        </div>
        <div className='flex items-center flex-wrap gap-10'>
            {selectedEmployees?.length ?
                renderThis :
                <TwinTrans transKey='haventAddEmployees'>No has añadido empleados</TwinTrans>
            }
        </div>
    </LittleModalCreateEdit>)
}

interface ModalCreateEmployeeWorkingCenterLogicProps {
    onSubmit: OnSubmit
    setOpened: ModalOpenedSetTy
    preSelEmployees: string[]
    dictWorkingCenterStations: TwinDictionary
}

const useModalCreateEmployeeWorkingCenterLogic = ({ preSelEmployees, setOpened, onSubmit, dictWorkingCenterStations }: ModalCreateEmployeeWorkingCenterLogicProps) => {
    const { id } = useParams()

    const [formData, setFormData] = useState({ EmployeeId: null, is_default: false, defaultWorkingCenterStationId: Object.keys(dictWorkingCenterStations)?.[0]})
    const [employees, setEmployees] = useState<TwinDictionary>({})
    const [selectedEmployees, setSelectedEmployees] = useState<WorkingCenterEmployeeWithDefaultTy[]>([])
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>(preSelEmployees || [])

    const changeFields = useCallback((field: 'EmployeeId' | 'is_default' | 'defaultWorkingCenterStationId', val: any) => {
        setFormData((old)=> {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[field] = val
            return {...copyOld}
        })
    }, [setFormData])

    const assignEmployeesToWorkingCenter = useCallback(async () => {
        const employees = JSON.stringify(selectedEmployees)
        const res = await twinFetchPostJSON('/api/app/workingCenter/workingCenterEmployee/bulkAssignEmployeeToWorkingCenter', { employees })
        if(res){
            setOpened(null)
            onSubmit?.(res, {})
        }
    }, [selectedEmployees, setOpened, onSubmit])

    const handleAddEmployee = useCallback(async() => {
        await flushSync(async () => {
            if(formData.EmployeeId){
                let add = false
                await setSelectedEmployeeIds((old)=>{
                    const copyOld = JSON.parse(JSON.stringify(old))
                    if(!copyOld.includes(formData.EmployeeId)){
                        copyOld.push(formData.EmployeeId)
                        add = true
                    }
                    return [...copyOld]
                })
                if(add){
                    await setSelectedEmployees((old)=>{
                        const copyOld = JSON.parse(JSON.stringify(old))
                        copyOld.push({ WorkingCenterId: id, EmployeeId: formData.EmployeeId, is_default: formData.is_default, defaultWorkingCenterStationId: formData.defaultWorkingCenterStationId })
                        return [...copyOld]
                    })
                    await setFormData({ EmployeeId: null, is_default: false, defaultWorkingCenterStationId: Object.keys(dictWorkingCenterStations)?.[0] })
                }
            }
        })
    }, [setSelectedEmployees, id, setSelectedEmployeeIds, setFormData, formData, dictWorkingCenterStations])

    const handleRemoveEmployee = useCallback((posArr: number, EmployeeId: string) => {
        setSelectedEmployees((old)=>{
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.splice(posArr, 1)
            return [...copyOld]
        })
        setSelectedEmployeeIds((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            for (const key in copyOld) {
                const id = copyOld[key]
                if (id === EmployeeId){
                    copyOld.splice(key, 1)
                }
            }
            return [...copyOld]
        })
    }, [setSelectedEmployees, setSelectedEmployeeIds])

    const getEmployees = useCallback(async () => {
        const data = await twinFetchPostJSON('/api/app/employee/getAllEmployees', { where: { 'id': { '$not': preSelEmployees } } })
        const dictEmployees = dictionaryComplexFromJsonArr(data)
        setEmployees(dictEmployees)
    }, [setEmployees, preSelEmployees])

    useEffect(() => {
        getEmployees()
    }, [getEmployees])

    return { employees, id, selectedEmployees, handleAddEmployee, handleRemoveEmployee, assignEmployeesToWorkingCenter, selectedEmployeeIds, changeFields, formData }
}

interface EmployeeSelectedProps {
    EmployeeId: string
    name: string
    posArr: number
    removeEmployee: (posArr: number, EmployeeId: string)=> void
}

const EmployeeSelected: React.FC<EmployeeSelectedProps> = ({ name, posArr, removeEmployee, EmployeeId }) => {
    return (
        <div className='flex items-center py-5 px-10 bg-gray-EF'>
            <span>{name}</span>
            <TwinIcon className='ml-10 cursor-pointer hover:text-red-BA' icon={faXmark} onClick={() => removeEmployee(posArr, EmployeeId)} />
        </div>
    )
}

export default ModalCreateEmployeeWorkingCenter;